import { watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/store/user'
import { useSocietyStore } from '@/store/society'
import { Api } from '@/utils/api'

/**
 * Composable for managing the Blar Support Widget
 * Handles user identification and language synchronization
 */
export function useBlarSupportWidget(): {
  identifyUser: (userId: string, email: string, name?: string, phone?: string) => Promise<void>
  clearUser: () => void
  updateLanguage: (language: string) => Promise<void>
} {
  const { locale } = useI18n()
  const userStore = useUserStore()
  const societyStore = useSocietyStore()

  let widgetCheckInterval: ReturnType<typeof setInterval> | null = null
  let widgetInstance: SupportWidget | undefined = undefined

  /**
   * Wait for widget to be available and return the instance
   */
  const getWidget = (): Promise<SupportWidget | undefined> => {
    return new Promise((resolve) => {
      if (window.getSupportWidget) {
        const widget = window.getSupportWidget()
        if (widget) {
          resolve(widget)
          return
        }
      }

      // Poll for widget availability
      let attempts = 0
      const maxAttempts = 50 // 5 seconds max wait
      const interval = setInterval(() => {
        attempts++
        if (window.getSupportWidget) {
          const widget = window.getSupportWidget()
          if (widget) {
            clearInterval(interval)
            resolve(widget)
            return
          }
        }
        if (attempts >= maxAttempts) {
          clearInterval(interval)
          resolve(undefined)
        }
      }, 100)
    })
  }

  /**
   * Fetch the HMAC identity signature from our authenticated backend.
   * The backend signs the session user's email (the same value we pass as
   * external_id below), so Blar can verify the identity. Returns undefined on
   * failure so the widget still loads (unverified) until Blar enforces auth.
   * Security context: CWE-306 — see .context/blar-security-CWE-306.md.
   */
  const fetchIdentitySignature = (): Promise<string | undefined> => {
    return new Promise((resolve) => {
      Api.get(
        'users/blar/identity-signature/',
        () => {},
        (response: { data: { identity_signature?: string } }) => {
          resolve(response.data.identity_signature)
        },
        () => {
          console.error('Failed to fetch Blar identity signature')
          resolve(undefined)
        }
      )
    })
  }

  /**
   * Identify user in the support widget
   */
  const identifyUser = async (
    userId: string,
    email: string,
    name?: string,
    phone?: string
  ): Promise<void> => {
    const widget = await getWidget()
    if (widget) {
      try {
        // external_id (userId) must match exactly what the backend signs (the
        // session user's email, no alias stripping) or Blar rejects the signature.
        const identitySignature = await fetchIdentitySignature()
        await widget.setUser(
          userId,
          email,
          name,
          phone,
          societyStore.name,
          societyStore.companyId,
          identitySignature ?? null,
          { url: window.location.href }
        )
        widgetInstance = widget
      } catch (error) {
        console.error('Failed to identify user in Blar widget:', error)
      }
    }
  }

  /**
   * Clear user identification
   */
  const clearUser = (): void => {
    if (widgetInstance) {
      try {
        widgetInstance.clearUser()
      } catch (error) {
        console.error('Failed to clear user in Blar widget:', error)
      }
    }
  }

  /**
   * Update widget language
   */
  const updateLanguage = async (language: string): Promise<void> => {
    const widget = await getWidget()
    if (widget) {
      try {
        widget.setLanguage(language)
      } catch (error) {
        console.error('Failed to set Blar widget language:', error)
      }
    }
  }

  /**
   * Auto-identify user when user store changes
   */
  const setupUserWatcher = (): void => {
    watch(
      () => [userStore.email, userStore.name, societyStore.id, societyStore.companyId],
      async ([email, name]) => {
        if (email) {
          // Use email as userId if no separate ID is available
          await identifyUser(String(email), String(email), name ? String(name) : undefined)
        } else {
          clearUser()
        }
      },
      { immediate: true }
    )
  }

  /**
   * Update widget language when i18n locale changes
   */
  const setupLanguageWatcher = (): void => {
    watch(
      () => locale.value,
      async (newLocale) => {
        // Map locale codes: 'pt-BR' -> 'pt', others stay the same
        const widgetLanguage = newLocale === 'pt-BR' ? 'pt' : newLocale
        await updateLanguage(widgetLanguage)
      },
      { immediate: true }
    )
  }

  onMounted(() => {
    setupUserWatcher()
    setupLanguageWatcher()

    // Initial widget check
    getWidget().then((widget) => {
      if (widget && userStore.email) {
        identifyUser(userStore.email, userStore.email, userStore.name)
      }
    })
  })

  onUnmounted(() => {
    if (widgetCheckInterval) {
      clearInterval(widgetCheckInterval)
      widgetCheckInterval = null
    }
  })

  return {
    identifyUser,
    clearUser,
    updateLanguage
  }
}

